import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, LogOut, LayoutDashboard, Shield, ArrowRight, ChevronDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import MasLogo from "./MasLogo";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/lookup", label: "Verify Cert" },
  { to: "/submit", label: "Submit an Item" },
];

const menus = [
  { label: "Company", links: [["Who We Are", "/about"], ["Our Process", "/process"], ["The MAS Standard", "/standard"], ["Newsroom", "/press"], ["Contact Us", "/contact"]] },
  { label: "Resources", links: [["Grading Guide", "/trading-card-grading"], ["Why MAS", "/why-mas"], ["Compare Grading", "/compare"], ["Witnessed Certification", "/witnessed-certification"], ["Grading Scale", "/grading-scale"], ["Card Grades Explained", "/card-grades"], ["Population Report", "/lookup"], ["FAQ", "/faq"], ["MAS Certified, Inc.", "https://mascertified.com"]] },
];

const MenuLink = ({ to, label, onClick }) =>
  to.startsWith("http")
    ? <a href={to} target="_blank" rel="noreferrer" onClick={onClick} className="block px-3 py-2.5 rounded-xl text-sm font-medium text-neutral-600 hover:bg-neutral-100 hover:text-[#0A0A0A] transition-colors duration-200">{label}</a>
    : <Link to={to} onClick={onClick} className="block px-3 py-2.5 rounded-xl text-sm font-medium text-neutral-600 hover:bg-neutral-100 hover:text-[#0A0A0A] transition-colors duration-200">{label}</Link>;

const PROMOS = ["Save 10% on all online submissions — Code: GRADE10", "Tamper-evident graded slabs", "Live population registry", "Trusted by collectors, dealers & auction houses"];

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <div className="bg-[#0A0A0A] text-white overflow-hidden no-print">
        <div className="marquee whitespace-nowrap py-2.5 text-[11px] font-semibold tracking-[0.12em] uppercase flex">
          {Array.from({ length: 2 }).map((_, i) => (
            <span key={i} className="flex shrink-0">
              {PROMOS.map((t, j) => (<span key={j} className="mx-6 flex items-center gap-3"><span className="text-white/30">•</span>{t}</span>))}
            </span>
          ))}
        </div>
      </div>

      <header className={`sticky top-0 z-50 bg-white transition-shadow duration-300 no-print ${scrolled ? "border-b border-neutral-200 shadow-[0_2px_20px_-8px_rgba(0,0,0,0.15)]" : "border-b border-neutral-100"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-[78px] flex items-center justify-between gap-6">
          <Link to="/" data-testid="nav-logo" className="shrink-0"><MasLogo tone="dark" size="md" /></Link>

          <nav className="hidden lg:flex items-center gap-7">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-sm font-semibold transition-colors duration-300 ${loc.pathname === l.to ? "text-[#0A0A0A]" : "text-neutral-500 hover:text-[#0A0A0A]"}`}>
                {l.label}
              </Link>
            ))}
            {menus.map((m) => (
              <div key={m.label} className="relative group" data-testid={`nav-menu-${m.label.toLowerCase()}`}>
                <button className="flex items-center gap-1 text-sm font-semibold text-neutral-500 group-hover:text-[#0A0A0A] transition-colors duration-300">
                  {m.label} <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-300" />
                </button>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 hidden group-hover:block">
                  <div className="w-56 bg-white rounded-2xl border border-neutral-200 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.3)] p-2">
                    {m.links.map(([label, to]) => <MenuLink key={label} to={to} label={label} />)}
                  </div>
                </div>
              </div>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4 shrink-0">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="nav-admin" className="flex items-center gap-1.5 text-sm font-medium text-neutral-500 hover:text-[#0A0A0A] transition-colors duration-300"><Shield size={15} /> Admin</Link>
                )}
                <Link to="/portal" data-testid="nav-portal" className="flex items-center gap-1.5 text-sm font-medium text-neutral-500 hover:text-[#0A0A0A] transition-colors duration-300"><LayoutDashboard size={15} /> Portal</Link>
                <button data-testid="nav-logout" onClick={() => { logout(); nav("/"); }} className="flex items-center gap-1.5 text-sm font-medium text-neutral-500 hover:text-red-500 transition-colors duration-300"><LogOut size={15} /> Logout</button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login" className="text-sm font-semibold text-[#0A0A0A] hover:text-neutral-600 transition-colors duration-300">Sign In</Link>
            )}
            <Link to={user ? "/submit" : "/register"} data-testid="nav-cta"
              className="group flex items-center gap-2 px-6 py-3 text-sm font-bold bg-[#0A0A0A] text-white rounded-full hover:bg-neutral-800 transition-all duration-300">
              {user ? "Submit" : "Get Started"} <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform duration-300" />
            </Link>
          </div>

          <button className="lg:hidden text-[#0A0A0A]" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">{open ? <X /> : <Menu />}</button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-neutral-200 bg-white px-6 py-4 flex flex-col gap-1 max-h-[75vh] overflow-y-auto">
            {navLinks.map((l) => (<Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="py-2.5 font-semibold text-neutral-600 hover:text-[#0A0A0A]">{l.label}</Link>))}
            {menus.map((m) => (
              <div key={m.label} className="pt-2">
                <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-400 font-bold py-1.5">{m.label}</div>
                {m.links.map(([label, to]) => <MenuLink key={label} to={to} label={label} onClick={() => setOpen(false)} />)}
              </div>
            ))}
            <div className="border-t border-neutral-200 my-2" />
            {user ? (
              <>
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="py-2 font-semibold text-[#0A0A0A]">Admin</Link>}
                <Link to="/portal" onClick={() => setOpen(false)} className="py-2 font-semibold text-[#0A0A0A]">Portal</Link>
                <button onClick={() => { logout(); nav("/"); setOpen(false); }} className="py-2 text-left font-semibold text-red-500">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="py-2 font-semibold text-[#0A0A0A]">Sign In</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="mt-2 text-center py-3 font-bold bg-[#0A0A0A] text-white rounded-full">Get Started</Link>
              </>
            )}
          </div>
        )}
      </header>
    </>
  );
}
